import { useTranslation } from "@sagot/i18n";
import { EmptyState, ErrorState, Icon, Skeleton } from "@sagot/ui";
import type { BillingDetailsOut } from "../shared";
import { GatedButton } from "./gated-button";
import { useBillingDetails } from "./use-billing";

function addressLine(details: BillingDetailsOut): string {
  const a = details.address;
  return ["line1", "line2", "city", "province", "zip"]
    .map((key) => a[key])
    .filter((part): part is string => typeof part === "string" && part.length > 0)
    .join(", ");
}

function Item({ label, children, wide, mono }: { label: string; children: string; wide?: boolean; mono?: boolean }) {
  return (
    <div className={wide ? "sm:col-span-2" : undefined}>
      <dt className="text-xs font-semibold text-fg-subtle">{label}</dt>
      <dd className={mono ? "mt-0.5 font-mono" : "mt-0.5 font-semibold"}>{children}</dd>
    </div>
  );
}

function DetailsBody({ details }: { details: BillingDetailsOut }) {
  const { t } = useTranslation();
  const tax = details.tax_type ? t(`billing.details.tax_${details.tax_type}`, { defaultValue: details.tax_type }) : t("billing.details.taxUnset");
  const proof = details.proof_status ? ` · ${t(`billing.details.proof_${details.proof_status}`, { defaultValue: details.proof_status })}` : "";
  return (
    <dl className="card-body grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
      <Item label={t("billing.details.registeredName")}>{details.registered_name ?? "—"}</Item>
      <Item label={t("billing.details.tin")} mono>
        {details.tin ?? "—"}
      </Item>
      <Item label={t("billing.details.address")} wide>
        {addressLine(details) || "—"}
      </Item>
      <Item label={t("billing.details.receiptsTo")}>{details.receipt_email ?? "—"}</Item>
      <Item label={t("billing.details.taxType")}>{`${details.vat_exempt ? t("billing.details.vatExemptShort") : tax}${proof}`}</Item>
    </dl>
  );
}

/** Registered name / TIN / address printed on official receipts. */
export function BillingDetailsCard({ canManage, onEdit }: { canManage: boolean; onEdit: (details: BillingDetailsOut) => void }) {
  const { t } = useTranslation();
  const details = useBillingDetails();
  const empty = details.data && !details.data.registered_name && !details.data.tin;
  return (
    <section className="card" aria-labelledby="details-title">
      <div className="card-header">
        <div>
          <h2 id="details-title" className="card-title">
            {t("billing.details.title")}
          </h2>
          <p className="mt-0.5 text-xs text-fg-subtle">{t("billing.details.subtitle")}</p>
        </div>
        <GatedButton allowed={canManage && Boolean(details.data)} variant="secondary" size="sm" onClick={() => { if (details.data) onEdit(details.data); }}>
          <Icon name="pencil" />
          {t("billing.details.edit")}
        </GatedButton>
      </div>
      {details.isPending ? (
        <div className="card-body grid grid-cols-1 gap-3 sm:grid-cols-2" aria-hidden="true">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-10" />
          ))}
        </div>
      ) : details.isError ? (
        <ErrorState headingLevel={3} title={t("billing.details.loadError")} onRetry={() => void details.refetch()} retryLabel={t("common.retry")} />
      ) : empty ? (
        <EmptyState icon="file-text" headingLevel={3} title={t("billing.details.emptyTitle")} description={t("billing.details.emptyBody")} />
      ) : (
        <DetailsBody details={details.data} />
      )}
    </section>
  );
}
